import { useState, useRef, useEffect, useCallback } from "react";
import { useFearGreed } from "@/hooks/use-fear-greed";
import { useFearGreedHistory } from "@/hooks/use-fear-greed-history";
import { useDailyContext } from "@/hooks/use-daily-context";
import { getRitualMessage } from "@/data/ritual-messages";
import { formatDistanceToNow } from "date-fns";
import { es, it, fr, de, pt } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { useT } from "@/i18n/useT";
import InfoDrawer from "@/components/InfoDrawer";
import PulseStrip from "@/components/PulseStrip";
import TapeStrip from "@/components/TapeStrip";
import ButtonDome from "@/components/ButtonDome";
import LanguageSelector from "@/components/LanguageSelector";
import SeoHead from "@/components/SeoHead";
import type { SentimentBucket, MotionPhase } from "@/lib/fear-greed";
import type { MessageKey } from "@/i18n/messages";

const BUCKET_KEYS: Record<string, MessageKey> = {
  "Extreme Fear": "bucketExtremeFear",
  "Fear": "bucketFear",
  "Neutral": "bucketNeutral",
  "Greed": "bucketGreed",
  "Extreme Greed": "bucketExtremeGreed",
};

const dateFnsLocales: Record<string, typeof es> = { es, it, fr, de, pt };

export default function FearGreedButton() {
  const { data, isLoading, error, isFetching, isStale, tapButton, forceRefresh } = useFearGreed();
  const { t, locale } = useT();
  const [phase, setPhase] = useState<MotionPhase>("idle");
  const [ritualMessage, setRitualMessage] = useState<string | null>(null);
  const [bucketChanged, setBucketChanged] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevBucketRef = useRef<string | null>(null);
  const ritualTimer = useRef<ReturnType<typeof setTimeout>>();
  const releaseTimer = useRef<ReturnType<typeof setTimeout>>();
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
  const isLongPress = useRef(false);
  const tapCount = useRef(0);

  const { data: historyData } = useFearGreedHistory(drawerOpen);
  const { data: contextData } = useDailyContext(drawerOpen);

  useEffect(() => {
    if (!data) return;
    if (prevBucketRef.current !== null && prevBucketRef.current !== data.bucket) {
      setBucketChanged(true);
      setTimeout(() => setBucketChanged(false), 1000);
    }
    prevBucketRef.current = data.bucket;
  }, [data]);

  const [pulseMessage, setPulseMessage] = useState<string | null>(null);

  const showRitual = useCallback(() => {
    if (!data) return;
    tapCount.current += 1;

    const msg = getRitualMessage(
      data.bucket as SentimentBucket,
      data.value,
      locale,
      new Date(),
      tapCount.current
    );
    setRitualMessage(msg);
    const showPulse = tapCount.current % 3 === 0 && contextData?.pulse;
    setPulseMessage(showPulse ? contextData.pulse : null);
    if (!ritualTimer.current) {
      ritualTimer.current = setTimeout(() => {
        setRitualMessage(null);
        setPulseMessage(null);
        ritualTimer.current = undefined;
      }, 5000);
    }
  }, [data, locale, contextData]);

  const triggerPress = useCallback(() => {
    setPhase("pressing");
    navigator.vibrate?.(12);
    setTimeout(() => setPhase("pressed"), 120);
  }, []);

  const triggerRelease = useCallback(() => {
    setPhase("releasing");
    clearTimeout(releaseTimer.current);
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setPhase("idle"), 320);
  }, []);

  const handleTap = useCallback(async () => {
    if (isLongPress.current) return;
    triggerPress();
    showRitual();
    releaseTimer.current = setTimeout(() => triggerRelease(), 180);
    if (!isFetching) await tapButton();
  }, [isFetching, tapButton, showRitual, triggerPress, triggerRelease]);

  const handlePointerDown = useCallback(() => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(async () => {
      isLongPress.current = true;
      triggerPress();
      showRitual();
      releaseTimer.current = setTimeout(() => triggerRelease(), 180);
      await forceRefresh();
    }, 800);
  }, [forceRefresh, showRitual, triggerPress, triggerRelease]);

  const handlePointerUp = useCallback(() => {
    clearTimeout(longPressTimer.current);
    isLongPress.current = false;
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(ritualTimer.current);
      clearTimeout(releaseTimer.current);
      clearTimeout(settleTimer.current);
      clearTimeout(longPressTimer.current);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-button-bg">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/50" />
        <p className="text-xs text-muted-foreground/40 mt-3 font-medium">{t("loading")}</p>
      </div>
    );
  }

  if (!data && (error || !isLoading)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-button-bg px-6">
        <div className="text-center">
          <p className="text-base font-medium mb-2 text-foreground">{t("unableToLoad")}</p>
          <p className="text-sm text-muted-foreground mb-5">{t("somethingWentWrong")}</p>
          <button
            onClick={() => forceRefresh()}
            className="px-5 py-2.5 rounded-full bg-foreground/10 text-foreground text-sm font-medium hover:bg-foreground/15 transition-colors"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  const dateFnsLocale = dateFnsLocales[locale] ? { locale: dateFnsLocales[locale] } : undefined;
  const lastUpdated = formatDistanceToNow(new Date(data.timestamp), {
    addSuffix: true,
    ...dateFnsLocale,
  });

  const historyPoints = historyData?.points ?? [];
  const last7 = historyPoints.slice(0, 7).reverse().map((p) => p.value);
  const last30 = historyPoints.slice(0, 30).reverse().map((p) => p.value);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center select-none overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at 50% 40%, hsl(var(--muted) / 0.5), hsl(var(--button-bg)))`,
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <SeoHead title={t("metaTitle")} description={t("metaDescription")} />

      {/* Top-right controls */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5" style={{ marginTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="backdrop-blur-sm bg-background/30 rounded-full">
          <LanguageSelector />
        </div>
      </div>

      {/* Centered hero group: button + meta + signals */}
      <div className="relative flex flex-col items-center mt-[3vh]">

        {/* Ambient glow */}
        {data?.color && (
          <div
            className="absolute rounded-full pointer-events-none animate-[ambient-glow_4s_ease-in-out_infinite]"
            style={{
              width: 200,
              height: 200,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -60%)",
              background: `radial-gradient(circle, ${data.color}20 0%, transparent 70%)`,
            }}
          />
        )}

        {/* Button dome — all overlays (hint + ritual) are absolute so nothing shifts */}
        <div className="relative flex flex-col items-center">
          {/* Hint — above button, playful bounce */}
          {!ritualMessage && (
            <div
              className="absolute -top-14 left-1/2 z-10 pointer-events-none animate-[hint-bounce_2.4s_ease-in-out_infinite]"
              style={{ transform: "translateX(-50%)" }}
            >
              <span className="text-[13px] uppercase tracking-[0.15em] text-foreground/50 font-bold whitespace-nowrap">
                {t("hintTap")}
              </span>
            </div>
          )}

          <ButtonDome
            data={data}
            phase={phase}
            bucketChanged={bucketChanged}
            onTap={handleTap}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            ariaLabel={t("ariaButton", { value: data.value, bucket: t((BUCKET_KEYS[data.bucket] || "bucketNeutral") as MessageKey).toUpperCase() })}
            translatedBucket={t((BUCKET_KEYS[data.bucket] || "bucketNeutral") as MessageKey)}
          />

          {/* Ritual message — absolute overlay above button, never shifts layout */}
          {ritualMessage && (
            <div
              className="absolute left-1/2 z-10 pointer-events-none w-[min(80vw,320px)] animate-[ritual-fade_5s_ease-out_forwards]"
              style={{ bottom: "calc(100% + 16px)" }}
            >
              <div
                className="backdrop-blur-xl rounded-xl px-6 py-4 text-center shadow-lg"
                style={{
                  background: "hsl(var(--card) / 0.8)",
                  borderWidth: 1,
                  borderColor: "hsl(var(--border) / 0.3)",
                }}
              >
                <p className="text-foreground text-[15px] font-semibold tracking-wide leading-relaxed">
                  {ritualMessage}
                </p>
                {pulseMessage && (
                  <p className="text-muted-foreground text-xs mt-1.5 font-medium italic leading-snug">
                    {pulseMessage}
                  </p>
                )}
                <p
                  className="text-[8px] mt-1.5 font-medium uppercase tracking-widest"
                  style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}
                >
                  {t("ritualDisclaimer")}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Meta + signals below button — always anchored, never moves */}
        <div className="flex flex-col items-center mt-4">
          <span className="text-[11px] text-muted-foreground/55 font-medium tracking-wide text-center">
            {t("lastUpdated", { time: lastUpdated })}
            {isStale && (
              <span className="ml-1.5 text-[10px] text-caution/70">
                · {t("staleIndicator")}
              </span>
            )}
          </span>

          <span className="text-[9px] text-muted-foreground/30 font-medium tracking-wider uppercase text-center mt-0.5">
            {t("sourceLabel")}
          </span>

          {/* Signals group — anchored with divider */}
          <div className="flex flex-col items-center gap-2 mt-4 pt-3 border-t border-muted-foreground/[0.06] w-[min(70vw,300px)]">
            <PulseStrip contextPulse={contextData?.pulse} />
            <TapeStrip />
            <InfoDrawer
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              data={data}
              last7={last7}
              last30={last30}
              contextData={contextData}
            />
          </div>
        </div>
      </div>

      {/* Fetching indicator */}
      {isFetching && (
        <span className="absolute bottom-8 text-xs text-muted-foreground/40 font-medium animate-pulse tracking-wide" style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}>
          {t("updating")}
        </span>
      )}
    </div>
  );
}
